<template>
  <div>
    <vue-headful :title="this.setPageTitle('scoringProfilesList')" />
    <loading :active="getLoading" :color="'blue'" is-full-page loader="spinner"> </loading>
    <b-container class="my-4">
      <!-- HEADER -->
      <custom-flex-header :parent-node-class="['my-4']">
        <template v-slot:left-content>
          <h1 class="mb-0 h4 font-weight-normal">{{ $t('scoringProfilesList.scoringProfilesListTitle') }}</h1>
        </template>
        <template v-slot:right-content>

          <b-button size="sm" variant="primary" @click="goToNewProfile">
            {{ $t('scoringProfilesList.goToNewProfile') }}
          </b-button>
        </template>
      </custom-flex-header>

      <!-- TABLE FILTER -->
      <b-row class="mb-4">
        <b-col>
          <table-filters>
            <template v-slot:search>
              <label
                class="fieldLabel"
                for="name"
                v-t="'scoringProfilesList.scoringProfilesListTable.filterText'"
              ></label>
              <custom-search-bar
                :debounce="800"
                :text-value="getSearchBarValue"
                :placeholder="$t('scoringProfilesList.searchByScoringProfile')"
                @on-search="onSearchScoringProfile"
              ></custom-search-bar>
            </template>
            <template v-slot:filteredItems>
              <span class="text-secondary align-middle">
                <span class="font-weight-bold">{{ getPagination.items }}</span>
                {{ $tc('scoringProfilesList.scoringProfilesListTable.filteredProfiles', getPagination.items) }}
              </span>
            </template>
            <template v-slot:resetButton>
              <div>
                <b-button @click="onResetFilters" variant="link" :disabled="!hasSearched" size="sm" class="ml-2">
                  <span>{{ $t('search.label.resetFilters') }} <i class="fa fa-trash small ml-1 icon-size-10"></i></span>
                </b-button>
              </div>
            </template>
          </table-filters>
        </b-col>
      </b-row>

      <template v-if="getScoringProfilesList.length > 0">
        <b-row class="mb-4">
          <b-col>
            <div class="ml-auto">
              <table-pagination
                :pagination="getPagination"
                :pagination-options="pagination_options"
                :pagination-text="getPaginationItemsCounter"
                :products-per-page="$t('scoringProfilesList.perPage')"
                :pagination-class="['mb-4']"
                @on-change-per-page="onChangePerPage"
                @on-change-current-page="onChangeCurrentPage"
              >
              </table-pagination>
            </div>
          </b-col>
        </b-row>

        <!-- TABLE SCORING PROFILES -->
        <b-row class="mb-4">
          <b-col>
            <b-table
              class="list-table profiles-list-table mb-0"
              hover
              striped
              no-local-sorting
              no-sort-reset
              :fields="fields"
              :tbody-tr-class="['']"
              :items="getScoringProfilesList"
              @sort-changed="onSortingChanged"
            >
              <template v-slot:cell(scoringProfileModelName)="{ item }">
                <div v-if="item.scoringDefinitionWithModel.scoringProfileModelId">
                  <span> {{ item.scoringDefinitionWithModel.scoringProfileModelName }}</span>
                </div>
                <div v-else>
                  <span>{{ $t('scoringProfilesList.scoringProfilesListTable.customProfile') }}</span>
                </div>
              </template>
              <template v-slot:cell(profileCoverage)="{ item }">
                <!-- profile coverage -->
                <div v-if="'profileCoverage' in item">
                  <div v-for="(k, i) in Object.keys(item.profileCoverage)" :key="i">
                    <span class="mr-2">{{ $t('scoringProfiles.' + k) }}: </span>
                    <span>{{ item.profileCoverage[k] }}</span>
                  </div>
                </div>
              </template>
              <template v-slot:cell(insight)="{ item }">
                <!-- insight -->
                <router-link :to="goToInsight(item.scoringProfileId)">
                  <span class="mr-2">{{ $t('scoringProfilesList.scoringProfilesListTable.results') }}</span>
                  <i class="fas fa-long-arrow-right"></i>
                </router-link>
              </template>
              <template v-slot:cell(actions)="{ item }">
                <!-- actions -->
                <div class="text-right">
                  <b-dropdown size="md" toggle-class="text-decoration-none button-primary-class p-3" right no-caret>
                    <template #button-content>
                      <i class="fas fa-ellipsis-v"></i>
                      <span class="sr-only">{{
                        $t('scoringProfilesList.scoringProfilesListTable.columns.actions')
                      }}</span>
                    </template>
                    <b-dropdown-item-button @click="goToEditingProfile(item.scoringProfileId, 'update')">
                      <small>{{ $t('scoringProfilesList.scoringProfilesListActions.update') }}</small>
                    </b-dropdown-item-button>
                    <b-dropdown-item-button @click="goToEditingProfile(item.scoringProfileId, 'duplicate')">
                      <small>{{ $t('scoringProfilesList.scoringProfilesListActions.duplicate') }}</small>
                    </b-dropdown-item-button>
                    <b-dropdown-item-button @click="goToDeleteProfile(item.scoringProfileId)">
                      <small>{{ $t('scoringProfilesList.scoringProfilesListActions.delete') }}</small>
                    </b-dropdown-item-button>
                  </b-dropdown>
                </div>
              </template>
            </b-table>
          </b-col>
        </b-row>

        <!-- PAGINATION -->
        <b-row class="mb-4" v-if="getPagination.items > getPagination.perPage">
          <b-col cols="12">
            <div class="float-right">
              <b-pagination
                class="mb-0"
                :value="getPagination.currentPage"
                :total-rows="getPagination.items"
                :per-page="getPagination.perPage"
                @change="onChangeCurrentPage"
              >
              </b-pagination>
            </div>
          </b-col>
        </b-row>
      </template>
      <template v-else>
        <no-content
          :icon-name="'exclamation-circle'"
          :icon-class="['mb-3', 'fa-3x']"
          :text="$t('scoringProfilesList.noContent')"
        >
        </no-content>
      </template>
    </b-container>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import CustomSearchBar from '../../components/ScoringProfiles/CustomSearchBar.vue'
import TablePagination from '../../components/ScoringProfiles/TablePagination.vue'
import CustomFlexHeader from '../../components/Lib/CustomFlexHeader.vue'
import TableFilters from '../../components/Lib/TableFilters.vue'
import NoContent from '../../components/Lib/NoContent.vue'
const { mapActions, mapGetters, mapMutations } = createNamespacedHelpers('scoringProfilesList')
import { formatDate } from '../../utils/formatter'

export default {
  components: {
    CustomFlexHeader,
    CustomSearchBar,
    TablePagination,
    TableFilters,
    NoContent,
  },

  data() {
    return {
      pagination_options: [25, 100, 500, 1000],
    }
  },
  computed: {
    ...mapGetters(['getPagination', 'getScoringProfilesList', 'getSearchBarValue']),

    getLoading: {
      get() {
        return this.$store.getters['scoringProfilesCommon/getLoading']
      },
    },

    getModelsPermission: {
      get() {
        return this.$store.getters['can_ViewScoringProfileModels']
      },
    },

    getPaginationItemsCounter: {
      get() {
        let counter = 1
        if (this.getPagination !== null && this.getPagination.items !== 0) {
          counter =
            this.getPagination.items <= this.getPagination.perPage
              ? 1
              : this.getPagination.perPage * (this.getPagination.currentPage - 1) + 1
        }

        return this.$t('scoringProfilesList.shown', {
          start: counter,
          end: Math.min(this.getPagination.perPage * this.getPagination.currentPage, this.getPagination.items),
          of: this.getPagination.items || 0,
        })
      },
    },

    hasSearched() {
      return this.getSearchBarValue.length > 0
    },

    fields: {
      get() {
        return [
          {
            key: 'scoringProfileName',
            sortKey: 'Name',
            class: ['name', 'align-middle', 'font-weight-bold'],
            sortable: true,
            label: this.$t('scoringProfilesList.scoringProfilesListTable.columns.name'),
          },
          {
            key: 'scoringProfileModelName',
            sortKey: 'ProfileModel',
            class: ['profileModel', 'align-middle'],
            sortable: true,
            label: this.$t('scoringProfilesList.scoringProfilesListTable.columns.scoringDefinition'),
          },
          {
            key: 'profileCoverage',
            class: ['profileCoverage', 'align-middle'],
            label: this.$t('scoringProfilesList.scoringProfilesListTable.columns.coverage'),
          },
          {
            key: 'lastEditedBy',
            sortKey: 'User',
            class: ['user', 'align-middle'],
            sortable: true,
            label: this.$t('scoringProfilesList.scoringProfilesListTable.columns.user'),
          },
          {
            key: 'lastEditedAt',
            class: ['last-edited-at', 'align-middle'],
            formatter: (value) => {
              return formatDate(value)
            },
            sortable: true,
            label: this.$t('scoringProfilesList.scoringProfilesListTable.columns.lastEdit'),
          },
          {
            key: 'insight',
            sortable: false,
            class: ['insight', 'align-middle'],
            label: this.$t('scoringProfilesList.scoringProfilesListTable.columns.insight'),
          },
          {
            key: 'actions',
            sortable: false,
            label: this.$t('scoringProfilesList.scoringProfilesListTable.columns.actions'),
            class: ['actions', 'align-middle'],
          },
        ]
      },
    },
  },
  methods: {
    ...mapMutations(['setSearchBarValue', 'setPaginationPerPage', 'setPaginationCurrentPage', 'setSortingValue']),
    ...mapActions(['loadScoringProfiles', 'deleteScoringProfile']),

    goToModels() {
      this.$router.push({ name: 'scoringProfilesModelsList' })
    },

    onResetFilters() {
      this.setSearchBarValue('')
      this.getPagination.currentPage === 1 ? this.loadScoringProfiles() : this.setPaginationCurrentPage(1)
    },

    goToNewProfile() {
      this.$store.dispatch('scoringProfilesCommon/newScoringProfile')
      this.$router.push({ name: 'scoringProfilesCreate' })
    },

    goToInsight(id) {
      const modelsList = this.$store.getters['scoringProfilesModelsList/getModelsListToAssociate']
      const routeData = this.$router.resolve({
        name: 'scoringProfilesDetails',
        params: { scoringProfileId: id.toString(), modelsList: modelsList },
      })
      // The path below the router base, as <router-link :to> expects it: `.href` already carries the
      // /{assetClass}/ prefix, which the link would then add a second time.
      return routeData.route.fullPath
    },

    goToEditingProfile(id, action) {
      this.$store.dispatch('scoringProfilesCommon/setIsDuplicate', action === 'duplicate')
      this.$store.dispatch('scoringProfilesCommon/setScoringProfileId', id)
      this.$router.push({
        name: 'scoringProfilesEdit',
        params: { id: action === 'update' ? id.toString() : 'copy_' + id },
      })
    },

    goToDeleteProfile(id) {
      this.$bvModal
        .msgBoxConfirm(this.$t('scoringProfilesList.modalMessages.confirmDelete'), {
          cancelTitle: this.$t('common.cancel'),
        })
        .then((value) => {
          if (value) {
            this.deleteScoringProfile(id).catch((err) => {
              this.httpErrorsHandler(err)
            })
          }
        })
    },

    onSearchScoringProfile(value) {
      this.setSearchBarValue(value)
      this.getPagination.currentPage === 1 ? this.loadScoringProfiles() : this.setPaginationCurrentPage(1)
    },

    onChangeCurrentPage(value) {
      this.setPaginationCurrentPage(value)
      this.loadScoringProfiles()
    },

    onChangePerPage(value) {
      this.setPaginationPerPage(value)
      if (this.getPagination.currentPage === 1) {
        this.loadScoringProfiles()
      }
    },

    onSortingChanged(data) {
      let val = data.sortDesc ? '-' + data.sortBy : data.sortBy
      this.setSortingValue(val)
      this.getPagination.currentPage === 1 ? this.loadScoringProfiles() : this.setPaginationCurrentPage(1)
    },
  },

  created() {
    this.loadScoringProfiles().catch((err) => {
      this.httpErrorsHandler(err)
    })
  },
}
</script>

<style lang="scss">
.page-link {
  background-color: #e9ecef;
}

.button-primary-class,
.button-primary-class:active {
  border: 0;
  color: var(--secondaryColor) !important;
  background-color: transparent !important;
}

.show > .button-primary-class {
  color: var(--secondaryColor) !important;
}

.page-item.disabled .page-link {
  background-color: #e9ecef;
}

// Scoped to this table: the same selector in the other lists is global CSS, and whichever file loads
// last would otherwise decide the column widths of all of them.
table.list-table.profiles-list-table {
  th,
  td {
    &.insight {
      width: 10%;
    }
    &.actions {
      width: 3%;
    }
    &.profileCoverage {
      width: 23%;
    }
    &.user {
      width: 14%;
    }
    &.last-edited-at {
      width: 14%;
    }
  }
}
</style>
